/**
 * React Query hooks for the read-only "AI Quality & Observability" admin page.
 *
 * The pure summarizers + types live in ./ai-quality-helpers.ts (no Supabase import, so
 * they stay Jest-testable). This file only owns the table reads. Everything is
 * read-only and RLS-gated.
 *
 * Mock/demo mode: each hook returns SAMPLE data (via ./ai-quality-helpers sample
 * generators) so the cockpit's figures look alive in a demo — matching the sibling
 * admin dashboards. PRODUCTION mode always reads the real tables (or an honest empty
 * state); there are no mock fallbacks where it counts.
 *
 * Queryable in production:
 *   - llm_usage_daily   (any admin, is_admin)          → AI Health Today
 *   - scoring_evals     (any admin, is_admin)           → Latest Eval Runs
 *   - analytics_events  (super_admin/analyst only)      → dd_rag_retrieval evidence
 */

import { useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { mode } from '@/lib/config/app-mode';
import {
  summarizeLatestRun,
  mapRetrievalEvents,
  sampleLlmUsageRows,
  sampleScoringRun,
  sampleRetrievalEvents,
  sampleObservabilityEvents,
  sampleEvalRuns,
  type LatestScoringRun,
  type ScoringEvalRow,
  type RagRetrievalResult,
  type ObservabilityEventRow,
  type EvalRunRow,
} from './ai-quality-helpers';
import type { LlmUsageRow } from './use-llm-usage';

export * from './ai-quality-helpers';

/**
 * Daily LLM usage rows for the AI Health section. Mirrors useLlmUsageDaily but adds
 * the mock/demo sample branch so the health figures populate in a demo.
 */
export function useHealthUsageRows(days = 30) {
  return useQuery({
    queryKey: ['admin', 'ai-quality', 'health-usage', days, mode.showSampleData],
    queryFn: async (): Promise<LlmUsageRow[]> => {
      if (mode.showSampleData) return sampleLlmUsageRows(Date.now());
      const since = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('llm_usage_daily')
        .select('*')
        .gte('day', since)
        .order('day', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: isSupabaseConfigured() || mode.showSampleData,
    staleTime: 60 * 1000,
  });
}

export function useLatestScoringEvals() {
  return useQuery({
    queryKey: ['admin', 'ai-quality', 'scoring-evals', mode.showSampleData],
    queryFn: async (): Promise<LatestScoringRun | null> => {
      if (mode.showSampleData) return sampleScoringRun(Date.now());
      const { data, error } = await supabase
        .from('scoring_evals')
        .select('run_id, methodology_version, case_name, expected_band, actual_band, final_score, in_band, spread, created_at')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return summarizeLatestRun((data ?? []) as ScoringEvalRow[]);
    },
    enabled: isSupabaseConfigured() || mode.showSampleData,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRecentRagRetrieval(limit = 25) {
  return useQuery({
    queryKey: ['admin', 'ai-quality', 'rag-retrieval', limit, mode.showSampleData],
    queryFn: async (): Promise<RagRetrievalResult> => {
      if (mode.showSampleData) {
        const events = sampleRetrievalEvents(Date.now());
        return { events, empty: events.length === 0 };
      }
      const { data, error } = await supabase
        .from('analytics_events')
        .select('properties, created_at')
        .eq('event_name', 'dd_rag_retrieval')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      const events = mapRetrievalEvents(data ?? []);
      return { events, empty: events.length === 0 };
    },
    enabled: isSupabaseConfigured() || mode.showSampleData,
    staleTime: 60 * 1000,
  });
}

/**
 * Recent PII-safe proof/observability events from ai_observability_events — the real,
 * queryable proof-event table that backs certification status + DD-flow evidence.
 */
export function useObservabilityEvents(limit = 500) {
  return useQuery({
    queryKey: ['admin', 'ai-quality', 'observability', limit, mode.showSampleData],
    queryFn: async (): Promise<ObservabilityEventRow[]> => {
      if (mode.showSampleData) return sampleObservabilityEvents(Date.now());
      const { data, error } = await supabase
        .from('ai_observability_events')
        .select('event_name, startup_id, document_id, metadata, created_at')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as ObservabilityEventRow[];
    },
    enabled: isSupabaseConfigured() || mode.showSampleData,
    staleTime: 60 * 1000,
  });
}

/**
 * Recent live-eval runs (RAG faithfulness / retrieval / extraction) from ai_eval_runs.
 * These persist when an operator runs a live eval with `--persist`; scoring history is
 * separate (scoring_evals → useLatestScoringEvals).
 */
export function useEvalRuns(limit = 40) {
  return useQuery({
    queryKey: ['admin', 'ai-quality', 'eval-runs', limit, mode.showSampleData],
    queryFn: async (): Promise<EvalRunRow[]> => {
      if (mode.showSampleData) return sampleEvalRuns(Date.now());
      const { data, error } = await supabase
        .from('ai_eval_runs')
        .select('run_id, suite, total, passed, failed, env, created_at')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as EvalRunRow[];
    },
    enabled: isSupabaseConfigured() || mode.showSampleData,
    staleTime: 5 * 60 * 1000,
  });
}
